import { create } from 'zustand'

import {
  refreshAccessToken,
  registerRefreshHandler,
  registerSessionExpiredHandler,
  setAccessToken,
} from '@/api/authTokenStore'
import { queryClient } from '@/api/queryClient'
import { useUiStore } from '@/stores/uiStore'
import type { AuthResult, CurrentUser } from '@/types/auth'
import { authApi } from '../api/authApi'

/**
 * Temaet kommer fra serveren, når vi ved hvem brugeren er.
 *
 * Browseren husker stadig valget lokalt — det er dét, der gør, at første
 * maling efter en genindlæsning ikke blinker hvid — men på en ny enhed er
 * localStorage tom, og så er brugerens eget valg det rigtige at følge.
 */
function applyUserTheme(user: CurrentUser): void {
  if (user.theme === 'light' || user.theme === 'dark') {
    useUiStore.getState().setTheme(user.theme)
  }
}

type AuthStatus =
  /** Ved opstart: vi ved endnu ikke, om der er en session. */
  | 'initialising'
  | 'authenticated'
  | 'anonymous'

interface AuthState {
  status: AuthStatus
  user: CurrentUser | null

  /**
   * Genopretter sessionen ved appens opstart via refresh token-cookien.
   * Access tokenet ligger kun i hukommelsen og overlever ikke en genindlæsning.
   */
  initialise: () => Promise<void>

  signIn: (result: AuthResult) => void
  signOut: () => Promise<void>
  setUser: (user: CurrentUser) => void
  switchOrganization: (organizationId: string) => Promise<void>

  hasPermission: (permission: string) => boolean
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'initialising',
  user: null,

  initialise: async () => {
    // Går gennem refreshAccessToken frem for at kalde authApi.refresh direkte.
    //
    // Fornyelsen roterer refresh tokenet, og serveren tolker et allerede
    // forbrugt token som tyveri: hele sessionens kæde tilbagekaldes. To
    // samtidige fornyelser logger derfor brugeren ud. refreshAccessToken
    // lægger samtidige kald sammen til ét — men det gjorde kun 401-vejen,
    // ikke opstarten.
    //
    // Og opstarten kaldes netop to gange: React StrictMode kører effekter to
    // gange i udvikling. Resultatet var, at en genindlæsning nogle gange
    // smed brugeren ud på loginsiden, afhængigt af hvilket af de to kald der
    // nåede først.
    const token = await refreshAccessToken()

    if (!token) {
      // Ingen session — det normale tilfælde for en anonym besøgende.
      // Handleren har allerede slugt fejlen og skelner ikke mellem "ikke
      // logget ind" og "serveren svarede ikke"; begge dele fører samme sted
      // hen for brugeren.
      setAccessToken(null)
      set({ status: 'anonymous', user: null })
    }
  },

  signIn: (result) => {
    setAccessToken(result.accessToken)
    set({ status: 'authenticated', user: result.user })
    applyUserTheme(result.user)
  },

  signOut: async () => {
    try {
      await authApi.logout()
    } catch {
      // Selv hvis serverkaldet fejler, skal den lokale session ryddes.
      // Ellers ville brugeren se sig selv som logget ind uden at være det.
    }

    setAccessToken(null)
    set({ status: 'anonymous', user: null })

    // Cachen indeholder den afmeldte brugers data. Blev den stående, ville
    // den næste, der logger ind på samme maskine, få serveret den forriges
    // projekter, til de enkelte queries selv fandt på at hente igen.
    queryClient.clear()

    // Samme grund: det sidst besøgte projekt er den forriges, ikke den næstes.
    useUiStore.getState().setLastProjectId(null)
  },

  setUser: (user) => {
    set({ user })
    applyUserTheme(user)
  },

  switchOrganization: async (organizationId) => {
    // Organisationen ligger i access tokenets claims, så skiftet kræver et nyt
    // token fra serveren – det kan ikke ske alene i frontend.
    const result = await authApi.switchOrganization(organizationId)
    get().signIn(result)

    // Query-nøglerne er ikke organisationsskoperet. Ryddes cachen ikke i
    // samme øjeblik som skiftet, serveres den gamle organisations lister,
    // til de bliver forældede af sig selv. Synkront efter signIn, så alle
    // efterfølgende hentninger sker med det nye token mod en tom cache.
    queryClient.clear()
  },

  hasPermission: (permission) => get().user?.permissions.includes(permission) ?? false,
}))

/**
 * Kobler HTTP-klienten sammen med auth-storen.
 *
 * Kaldes én gang ved opstart. Klienten kan ikke importere storen direkte
 * (cirkulær afhængighed), så forbindelsen laves her.
 */
export function connectAuthToHttpClient(): void {
  registerRefreshHandler(async () => {
    try {
      const result = await authApi.refresh()
      useAuthStore.getState().signIn(result)
      return result.accessToken
    } catch {
      return null
    }
  })

  registerSessionExpiredHandler(() => {
    setAccessToken(null)
    useAuthStore.setState({ status: 'anonymous', user: null })

    // En udløbet session er et logout, appen ikke selv bad om. Den næste,
    // der logger ind, er ikke nødvendigvis den samme — så cachen skal væk her
    // af samme grund som i signOut.
    queryClient.clear()
  })
}
